import { createElement as h, useEffect, useState, type ReactNode } from "react";
import type { OutlineSettings, PipelineDebugView, TonemapMode } from "../../../shared/api-contract";
import { fx, fxVec3, sc } from "../inspector/bindings";
import type { BindingCtx, DomainRead } from "../../../state/editor/types";
import type { Binding, InertTag, SectionDef } from "../inspector/schema";
import { CompactCheck, CompactColor, CompactSlider, Row } from "../inspector/rows";

/** `Row` requires `children` in props, so pass it explicitly. */
const row = (label: string, child: ReactNode, inert?: InertTag) => h(Row, { label, inert, children: child });

/** Both highlight flags exist only in OutlineSystem's defaults literal; the
 *  edge-overlay uniform block never reads either one. */
const OUTLINE_HIGHLIGHT_INERT: InertTag = {
  reason:
    "Stored, never read. OutlineSystem writes it into its settings object and the edge shader's uniforms never consult it \u2014 only Highlight, Intensity, Transparency, Colour and Debug reach the pass.",
};
import { LutControl } from "./custom/LutControl";
import { ReflectionMatrixControl } from "./custom/ReflectionMatrixGrid";

/**
 * Post Processing tab — the in-house frame pipeline (v12 surface). This file is
 * the reference shape for every data-driven section: each control is a RowDef
 * whose binding reads PostFXSettings and writes a PostFXSettingsPatch via fx().
 * `setPostFX` returns the full effective settings, so the store reconciles the
 * whole slice automatically and switches/selects always reflect reality.
 *
 * Defaults/ranges follow the iJewel EXHAUSTIVE reference §2.2 where our field
 * set lines up; engine-wave additions (DOF/CA/grain/LUT/TAA/SSCS/SSGI/SSBevel)
 * land here as later sections once contract v13+ ships.
 */

const TONEMAPS: { id: TonemapMode; label: string }[] = [
  { id: "ACES_FILMIC", label: "ACES Filmic" },
  { id: "UNCHARTED2", label: "Uncharted 2" },
  { id: "REINHARD", label: "Reinhard" },
  { id: "CINEON", label: "Cineon" },
  { id: "LINEAR", label: "Linear" },
  // v31 (webgi tonemap constant 0): no operator AND exposure is ignored,
  // distinct from "LINEAR" (no operator but still exposure-multiplied).
  { id: "NONE", label: "None" },
];

const DEBUG_VIEWS: { id: PipelineDebugView; label: string }[] = [
  { id: "off", label: "Off" },
  { id: "ao", label: "AO buffer" },
  { id: "ssr", label: "SSR buffer" },
  { id: "normals", label: "Normals" },
  { id: "depth", label: "Depth" },
  { id: "bloom", label: "Bloom" },
  { id: "accum", label: "Accumulation" },
  { id: "coc", label: "DOF CoC" },
  { id: "sscs", label: "Contact Shadows" },
  { id: "ssgi", label: "SSGI" },
  { id: "bevel", label: "Bevel Normals" },
];

/**
 * Outline (checklist places this in Post Processing, not Picking — it used to
 * be merged into the Picking tab's "Picker & Outline" panel). Reads/writes
 * OutlineSettings via getOutline/setOutline directly, independent of the
 * Picking-tab Picker section.
 */
function OutlineRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [o, setO] = useState<OutlineSettings | null>(null);
  useEffect(() => {
    r.getOutline().then(setO).catch(() => {});
  }, [r]);
  if (!o) return null;
  const set = (patch: Partial<OutlineSettings>) => r.setOutline(patch).then(setO);
  return h(
    "div",
    { className: "flex flex-col" },
    row("Outline", h(CompactCheck, { checked: o.enabled, onChange: (v: boolean) => void set({ enabled: v }) })),
    row("Highlight", h(CompactCheck, { checked: o.enableHighlight, onChange: (v: boolean) => void set({ enableHighlight: v }) })),
    row("Dynamic Selection", h(CompactCheck, { checked: o.enableDynamicSelection, onChange: (v: boolean) => void set({ enableDynamicSelection: v }) })),
    row("Intensity", h(CompactSlider, { value: o.intensity, min: 0, max: 2, step: 0.001, onChange: (v: number) => void set({ intensity: v }) })),
    row("Transparency", h(CompactSlider, { value: o.transparency, min: 0, max: 1, step: 0.01, onChange: (v: number) => void set({ transparency: v }) })),
    row("Color", h(CompactColor, { value: o.color, onChange: (v: string) => void set({ color: v }) })),
    row("Debug", h(CompactCheck, { checked: o.debugOutline, onChange: (v: boolean) => void set({ debugOutline: v }) })),
    row("Highlight Selected Materials", h(CompactCheck, { checked: o.highlightSelectedMaterials, onChange: (v: boolean) => void set({ highlightSelectedMaterials: v }) }), OUTLINE_HIGHLIGHT_INERT),
    row("Highlight Materials (same name)", h(CompactCheck, { checked: o.highlightSameMaterial, onChange: (v: boolean) => void set({ highlightSameMaterial: v }) }), OUTLINE_HIGHLIGHT_INERT),
  );
}

export const postfxSections: SectionDef[] = [
  {
    id: "ssr",
    title: "Screen Space Reflections",
    enabledBadge: fx((s) => s.ssr.enabled, (v) => ({ ssr: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.ssr.enabled, (v) => ({ ssr: { enabled: v } })) },
      { id: "intensity", kind: "slider", label: "Intensity", min: 0, max: 4, step: 0.01, binding: fx((s) => s.ssr.intensity, (v) => ({ ssr: { intensity: v } })) },
      { id: "steps", kind: "slider", label: "Step Count", min: 4, max: 64, step: 1, binding: fx((s) => s.ssr.steps, (v) => ({ ssr: { steps: v } })) },
      { id: "refineSteps", kind: "slider", label: "Refine Steps", min: 0, max: 8, step: 1, binding: fx((s) => s.ssr.refineSteps, (v) => ({ ssr: { refineSteps: v } })) },
      { id: "maxDistance", kind: "slider", label: "Ray Distance (× model)", min: 1, max: 12, step: 0.5, binding: fx((s) => s.ssr.maxDistanceScale, (v) => ({ ssr: { maxDistanceScale: v } })) },
      { id: "thickness", kind: "slider", label: "Thickness (× model)", min: 0.01, max: 0.2, step: 0.005, binding: fx((s) => s.ssr.thicknessScale, (v) => ({ ssr: { thicknessScale: v } })) },
      { id: "boost", kind: "vec3", label: "Boost", min: 0, max: 4, step: 0.01, axes: ["R", "G", "B"], binding: fxVec3((s) => s.ssr.boost, (v) => ({ ssr: { boost: v } })) },
      { id: "objectRadius", kind: "slider", label: "Object Radius", min: 0.01, max: 2, step: 0.01, visible: (read) => !(read("postFX") as { ssr: { autoRadius: boolean } }).ssr.autoRadius, binding: fx((s) => s.ssr.objectRadius, (v) => ({ ssr: { objectRadius: v } })) },
      { id: "autoRadius", kind: "checkbox", label: "Auto radius", binding: fx((s) => s.ssr.autoRadius, (v) => ({ ssr: { autoRadius: v } })) },
      { id: "power", kind: "slider", label: "Power", min: 0, max: 3, step: 0.05, binding: fx((s) => s.ssr.power, (v) => ({ ssr: { power: v } })) },
      { id: "tolerance", kind: "slider", label: "Tolerance", min: 0.1, max: 5, step: 0.05, binding: fx((s) => s.ssr.tolerance, (v) => ({ ssr: { tolerance: v } })) },
      { id: "lowQualityFrames", kind: "slider", label: "Low Quality Frames", min: 0, max: 4, step: 1, binding: fx((s) => s.ssr.lowQualityFrames, (v) => ({ ssr: { lowQualityFrames: v } })) },
      { id: "maskFrontRays", kind: "checkbox", label: "Ignore front rays", binding: fx((s) => s.ssr.maskFrontRays, (v) => ({ ssr: { maskFrontRays: v } })) },
      { id: "maskFront", kind: "slider", label: "Mask front rays factor", min: -1, max: 1, step: 0.01, binding: fx((s) => s.ssr.maskFrontFactor, (v) => ({ ssr: { maskFrontFactor: v } })) },
      { id: "rough", kind: "slider", label: "Glossy Convergence", min: 0, max: 1, step: 0.05, binding: fx((s) => s.ssr.roughnessJitter, (v) => ({ ssr: { roughnessJitter: v } })) },
      { id: "maxRough", kind: "slider", label: "Max Roughness", min: 0, max: 1, step: 0.05, binding: fx((s) => s.ssr.maxRoughness, (v) => ({ ssr: { maxRoughness: v } })) },
      { id: "fresnel", kind: "checkbox", label: "Fresnel (grazing boost)", binding: fx((s) => s.ssr.fresnel, (v) => ({ ssr: { fresnel: v } })) },
      { id: "halfRes", kind: "checkbox", label: "Half Resolution", binding: fx((s) => s.ssr.halfRes, (v) => ({ ssr: { halfRes: v } })) },
      // Per-source reflection weights (v30): how much a metal reflects each KIND
      // of surface. 1 = unchanged, 0 = that source vanishes from reflections.
      { id: "wBackground", kind: "slider", label: "Reflect Background", min: 0, max: 4, step: 0.05, binding: fx((s) => s.ssr.wBackground, (v) => ({ ssr: { wBackground: v } })) },
      { id: "wMetal", kind: "slider", label: "Reflect Metal", min: 0, max: 4, step: 0.05, binding: fx((s) => s.ssr.wMetal, (v) => ({ ssr: { wMetal: v } })) },
      { id: "wGem", kind: "slider", label: "Reflect Diamonds", min: 0, max: 4, step: 0.05, binding: fx((s) => s.ssr.wGem, (v) => ({ ssr: { wGem: v } })) },
      { id: "wGround", kind: "slider", label: "Reflect Ground", min: 0, max: 4, step: 0.05, binding: fx((s) => s.ssr.wGround, (v) => ({ ssr: { wGround: v } })) },
    ],
  },
  {
    // Checklist verbatim title. Folds together what used to be split across
    // "Post Effects" (tonemap/exposure/tonemapBg/tonemapGround/clipBg/
    // renderDepth) and "Color Grade" (contrast/saturation) — the checklist's
    // rpc annotation groups all of these under one Tonemapping field.
    id: "tonemapping",
    title: "Tonemapping",
    defaultCollapsed: true,
    rows: [
      {
        id: "tonemap",
        kind: "dropdown",
        label: "Tonemap",
        options: TONEMAPS,
        binding: fx((s) => s.tonemap, (v) => ({ tonemap: v as TonemapMode })),
      },
      {
        id: "exposure",
        kind: "slider",
        label: "Exposure",
        min: 0,
        max: 6.283,
        // 0.001 (not 0.01): (max-min)/step must be an integer or the native
        // range input silently clamps the settable max below what's shown
        // (liveness: set 6.283 → read back 6.28).
        step: 0.001,
        binding: sc((s) => s.toneMappingExposure, (v) => ({ toneMappingExposure: v }), (r, v) => r.setToneMappingExposure(v)),
      },
      { id: "tonemapBg", kind: "checkbox", label: "Tonemap Background", binding: fx((s) => s.tonemapBackground, (v) => ({ tonemapBackground: v })) },
      { id: "tonemapGround", kind: "checkbox", label: "Tonemap Ground", binding: fx((s) => s.tonemapGround, (v) => ({ tonemapGround: v })) },
      { id: "clipBg", kind: "checkbox", label: "Clip Background", binding: fx((s) => s.clipBackground, (v) => ({ clipBackground: v })) },
      { id: "renderDepth", kind: "checkbox", label: "Render Depth", binding: fx((s) => s.renderDepth, (v) => ({ renderDepth: v })) },
      { id: "contrast", kind: "slider", label: "Contrast", min: 0, max: 2, step: 0.01, binding: fx((s) => s.grade.contrast, (v) => ({ grade: { contrast: v } })) },
      { id: "saturation", kind: "slider", label: "Saturation", min: 0, max: 2, step: 0.01, binding: fx((s) => s.grade.saturation, (v) => ({ grade: { saturation: v } })) },
    ],
  },
  {
    id: "outline",
    title: "Outline",
    defaultCollapsed: true,
    rows: [{ id: "outlineControl", kind: "custom", label: "", component: OutlineRow }],
  },
  {
    id: "chromatic",
    title: "ChromaticAberration",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.chromaticAberration.enabled, (v) => ({ chromaticAberration: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.chromaticAberration.enabled, (v) => ({ chromaticAberration: { enabled: v } })) },
      { id: "intensity", kind: "slider", label: "Intensity", min: 0, max: 0.1, step: 0.001, binding: fx((s) => s.chromaticAberration.intensity, (v) => ({ chromaticAberration: { intensity: v } })) },
    ],
  },
  {
    id: "grain",
    title: "FilmicGrain",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.grain.enabled, (v) => ({ grain: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.grain.enabled, (v) => ({ grain: { enabled: v } })) },
      { id: "intensity", kind: "slider", label: "Intensity", min: 0, max: 20, step: 0.01, binding: fx((s) => s.grain.intensity, (v) => ({ grain: { intensity: v } })) },
      { id: "multiply", kind: "checkbox", label: "Multiply", binding: fx((s) => s.grain.multiply, (v) => ({ grain: { multiply: v } })) },
    ],
  },
  {
    id: "lut",
    title: "LUT",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.lut.enabled, (v) => ({ lut: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.lut.enabled, (v) => ({ lut: { enabled: v } })) },
      { id: "intensity", kind: "slider", label: "Intensity", min: 0, max: 1, step: 0.01, binding: fx((s) => s.lut.intensity, (v) => ({ lut: { intensity: v } })) },
      // Live on the wire the whole time and reachable from nowhere: defaulted
      // true in FramePipeline, pushed to the composite shader every frame,
      // branched on for background pixels, already in the setPostFX patch list,
      // and read back out of an imported iJewel vjson. With a LUT loaded the
      // backdrop was always graded and there was no way to exempt it.
      { id: "lutBackground", kind: "checkbox", label: "LUT Background", binding: fx((s) => s.lut.lutBackground, (v) => ({ lut: { lutBackground: v } })) },
      { id: "slots", kind: "custom", label: "", component: LutControl },
    ],
  },
  {
    id: "vignette",
    title: "Vignette",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.vignette.enabled, (v) => ({ vignette: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.vignette.enabled, (v) => ({ vignette: { enabled: v } })) },
      { id: "power", kind: "slider", label: "Power", min: 0.1, max: 4, step: 0.01, binding: fx((s) => s.vignette.power, (v) => ({ vignette: { power: v } })) },
      { id: "darkness", kind: "slider", label: "Darkness", min: 0, max: 1, step: 0.01, binding: fx((s) => s.vignette.darkness, (v) => ({ vignette: { darkness: v } })) },
      { id: "offset", kind: "slider", label: "Offset", min: 0, max: 1, step: 0.01, binding: fx((s) => s.vignette.offset, (v) => ({ vignette: { offset: v } })) },
      { id: "color", kind: "color", label: "Color", binding: fx((s) => s.vignette.color, (v) => ({ vignette: { color: v ?? "#000000" } })) as Binding<string | null> },
    ],
  },
  {
    id: "dof",
    title: "Depth of Field",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.dof.enabled, (v) => ({ dof: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.dof.enabled, (v) => ({ dof: { enabled: v } })) },
      { id: "focus", kind: "button", label: "Focus", buttons: [{ label: "Focus Center", icon: "center_focus_strong", run: (ctx) => ctx.ring.setDofFocus(0.5, 0.5) }] },
      // v59. Arms the viewer's own pointer handler, which already tells a click
      // from an orbit by a 3px threshold — so this can stay armed the way the
      // reference's does, without killing orbit.
      { id: "enableEdit", kind: "checkbox", label: "Enable Edit", binding: fx((s) => s.dof.enableEdit, (v) => ({ dof: { enableEdit: v } })) },
      { id: "depthRange", kind: "slider", label: "Depth Range", min: 0.1, max: 3, step: 0.05, binding: fx((s) => s.dof.depthRange, (v) => ({ dof: { depthRange: v } })) },
      { id: "nearBlur", kind: "slider", label: "Near Blur", min: 0, max: 1, step: 0.01, binding: fx((s) => s.dof.nearBlurScale, (v) => ({ dof: { nearBlurScale: v } })) },
      { id: "farBlur", kind: "slider", label: "Far Blur", min: 0, max: 1, step: 0.01, binding: fx((s) => s.dof.farBlurScale, (v) => ({ dof: { farBlurScale: v } })) },
      { id: "maxBlur", kind: "slider", label: "Max Blur (px)", min: 1, max: 48, step: 1, binding: fx((s) => s.dof.maxBlur, (v) => ({ dof: { maxBlur: v } })) },
      // The old text sent people to "Edit in the Picking tab" — a control that
      // never existed there; PickerSettings has four fields and none is it.
      { id: "dofNote", kind: "note", label: "", text: "Turn on Enable Edit, then click the model to focus on what you clicked. The focus point is held in world space, so orbiting keeps it sharp. Clicking the background focuses on the orbit centre instead." },
    ],
  },
  {
    id: "ssao",
    title: "SS Ambient Occlusion",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.ssao.enabled, (v) => ({ ssao: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.ssao.enabled, (v) => ({ ssao: { enabled: v } })) },
      { id: "intensity", kind: "slider", label: "Intensity", min: 0, max: 5, step: 0.05, binding: fx((s) => s.ssao.intensity, (v) => ({ ssao: { intensity: v } })) },
      { id: "autoRadius", kind: "checkbox", label: "Auto Radius", binding: fx((s) => s.ssao.autoRadius, (v) => ({ ssao: { autoRadius: v } })) },
      {
        id: "radiusScale",
        kind: "slider",
        label: "Radius (× model)",
        min: 0.02,
        max: 0.5,
        step: 0.01,
        visible: (read) => (read("postFX") as { ssao: { autoRadius: boolean } }).ssao.autoRadius,
        binding: fx((s) => s.ssao.radiusScale, (v) => ({ ssao: { radiusScale: v } })),
      },
      {
        id: "radius",
        kind: "slider",
        label: "Radius (world)",
        min: 0.1,
        max: 20,
        step: 0.1,
        visible: (read) => !(read("postFX") as { ssao: { autoRadius: boolean } }).ssao.autoRadius,
        binding: fx((s) => s.ssao.radius, (v) => ({ ssao: { radius: v } })),
      },
      // min 0 (was 0.00001): (max-min)/step must land on an integer step count
      // or the native range input can't reach max (liveness: set 0.01 → read
      // back 0.00951 at the old min/step grid).
      { id: "bias", kind: "slider", label: "Bias", min: 0, max: 0.01, step: 0.0001, binding: fx((s) => s.ssao.bias, (v) => ({ ssao: { bias: v } })) },
      { id: "falloff", kind: "slider", label: "Falloff", min: 0.01, max: 3, step: 0.05, binding: fx((s) => s.ssao.falloff, (v) => ({ ssao: { falloff: v } })) },
      { id: "samples", kind: "slider", label: "Num Samples", min: 4, max: 32, step: 1, binding: fx((s) => s.ssao.samples, (v) => ({ ssao: { samples: v } })) },
      { id: "edge", kind: "slider", label: "Edge Sharpness", min: 0, max: 1, step: 0.01, binding: fx((s) => s.ssao.edgeSharpness, (v) => ({ ssao: { edgeSharpness: v } })) },
      { id: "ground", kind: "slider", label: "Ground Darkening", min: 0, max: 1, step: 0.05, binding: fx((s) => s.ssao.groundDarkening, (v) => ({ ssao: { groundDarkening: v } })) },
      { id: "smooth", kind: "checkbox", label: "Smooth", binding: fx((s) => s.ssao.smoothEnabled, (v) => ({ ssao: { smoothEnabled: v } })) },
      { id: "halfRes", kind: "checkbox", label: "Half Resolution", binding: fx((s) => s.ssao.halfRes, (v) => ({ ssao: { halfRes: v } })) },
      { id: "cavity", kind: "checkbox", label: "Cavity (small-scale)", binding: fx((s) => s.ssaoCavity.enabled, (v) => ({ ssaoCavity: { enabled: v } })) },
      { id: "cavityIntensity", kind: "slider", label: "Cavity Intensity", min: 0, max: 5, step: 0.05, visible: (read) => (read("postFX") as { ssaoCavity: { enabled: boolean } }).ssaoCavity.enabled, binding: fx((s) => s.ssaoCavity.intensity, (v) => ({ ssaoCavity: { intensity: v } })) },
      { id: "cavityBrightness", kind: "slider", label: "Cavity Brightness", min: 0, max: 0.5, step: 0.01, visible: (read) => (read("postFX") as { ssaoCavity: { enabled: boolean } }).ssaoCavity.enabled, binding: fx((s) => s.ssaoCavity.brightness, (v) => ({ ssaoCavity: { brightness: v } })) },
    ],
  },
  {
    id: "ssbevel",
    title: "SSBevel",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.ssbevel.enabled, (v) => ({ ssbevel: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.ssbevel.enabled, (v) => ({ ssbevel: { enabled: v } })) },
      { id: "sceneBevel", kind: "checkbox", label: "Scene Bevel", binding: fx((s) => s.ssbevel.sceneBevel, (v) => ({ ssbevel: { sceneBevel: v } })) },
      { id: "radius", kind: "slider", label: "Scene Bevel Radius", min: 0, max: 8, step: 0.1, binding: fx((s) => s.ssbevel.radius, (v) => ({ ssbevel: { radius: v } })) },
    ],
  },
  {
    id: "bloom",
    title: "Bloom",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.bloom.enabled, (v) => ({ bloom: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.bloom.enabled, (v) => ({ bloom: { enabled: v } })) },
      { id: "threshold", kind: "slider", label: "Threshold", min: 0, max: 3, step: 0.01, binding: fx((s) => s.bloom.threshold, (v) => ({ bloom: { threshold: v } })) },
      { id: "soft", kind: "slider", label: "Soft Threshold", min: 0, max: 1, step: 0.01, binding: fx((s) => s.bloom.smoothing, (v) => ({ bloom: { smoothing: v } })) },
      { id: "intensity", kind: "slider", label: "Intensity", min: 0, max: 3, step: 0.01, binding: fx((s) => s.bloom.intensity, (v) => ({ bloom: { intensity: v } })) },
      { id: "iterations", kind: "slider", label: "Iterations", min: 2, max: 8, step: 1, binding: fx((s) => s.bloom.levels, (v) => ({ bloom: { levels: v } })) },
      { id: "radius", kind: "slider", label: "Radius", min: 0, max: 1, step: 0.01, binding: fx((s) => s.bloom.radius, (v) => ({ bloom: { radius: v } })) },
      { id: "power", kind: "slider", label: "Power", min: 0.2, max: 10, step: 0.05, binding: fx((s) => s.bloom.power, (v) => ({ bloom: { power: v } })) },
      { id: "debug", kind: "checkbox", label: "Debug", binding: fx((s) => s.bloom.debug, (v) => ({ bloom: { debug: v } })) },
    ],
  },
  {
    id: "ssgi",
    title: "SS Global Illumination (Dev)",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.ssgi.enabled, (v) => ({ ssgi: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled (Dev)", binding: fx((s) => s.ssgi.enabled, (v) => ({ ssgi: { enabled: v } })) },
      { id: "intensity", kind: "slider", label: "Intensity", min: 0, max: 4, step: 0.05, binding: fx((s) => s.ssgi.intensity, (v) => ({ ssgi: { intensity: v } })) },
      { id: "power", kind: "slider", label: "Power", min: 0, max: 3, step: 0.05, binding: fx((s) => s.ssgi.power, (v) => ({ ssgi: { power: v } })) },
      { id: "objectRadius", kind: "slider", label: "Object Radius (× model)", min: 0.5, max: 10, step: 0.5, binding: fx((s) => s.ssgi.objectRadius, (v) => ({ ssgi: { objectRadius: v } })) },
      // min 0 (was 0.0001): same step-grid reachability fix as SSAO Bias above.
      { id: "falloff", kind: "slider", label: "Falloff", min: 0, max: 4, step: 0.05, binding: fx((s) => s.ssgi.falloff, (v) => ({ ssgi: { falloff: v } })) },
      { id: "autoRadius", kind: "checkbox", label: "Auto radius", binding: fx((s) => s.ssgi.autoRadius, (v) => ({ ssgi: { autoRadius: v } })) },
      { id: "tolerance", kind: "slider", label: "Tolerance", min: 0.1, max: 5, step: 0.05, binding: fx((s) => s.ssgi.tolerance, (v) => ({ ssgi: { tolerance: v } })) },
      { id: "bias", kind: "slider", label: "Bias", min: -0.3, max: 0.3, step: 0.01, binding: fx((s) => s.ssgi.bias, (v) => ({ ssgi: { bias: v } })) },
      { id: "rayCount", kind: "slider", label: "Ray Count", min: 1, max: 5, step: 1, binding: fx((s) => s.ssgi.rayCount, (v) => ({ ssgi: { rayCount: v } })) },
      { id: "stepCount", kind: "slider", label: "Step Count", min: 1, max: 16, step: 1, binding: fx((s) => s.ssgi.stepCount, (v) => ({ ssgi: { stepCount: v } })) },
      { id: "smooth", kind: "checkbox", label: "Smooth Enabled", binding: fx((s) => s.ssgi.smoothEnabled, (v) => ({ ssgi: { smoothEnabled: v } })) },
      { id: "renderWithCamera", kind: "checkbox", label: "Render with Camera", binding: fx((s) => s.ssgi.renderWithCamera, (v) => ({ ssgi: { renderWithCamera: v } })) },
      { id: "ssgiEnabled", kind: "checkbox", label: "GI Enabled", binding: fx((s) => s.ssgi.ssgiEnabled, (v) => ({ ssgi: { ssgiEnabled: v } })) },
      { id: "halfRes", kind: "checkbox", label: "Half Resolution", binding: fx((s) => s.ssgi.halfRes, (v) => ({ ssgi: { halfRes: v } })) },
    ],
  },
  {
    id: "sscs",
    title: "Screen Space Contact Shadows",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.sscs.enabled, (v) => ({ sscs: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.sscs.enabled, (v) => ({ sscs: { enabled: v } })) },
      { id: "radius", kind: "slider", label: "Radius (× model)", min: 0.001, max: 0.1, step: 0.001, binding: fx((s) => s.sscs.radius, (v) => ({ sscs: { radius: v } })) },
      { id: "intensity", kind: "slider", label: "Intensity", min: 0, max: 1, step: 0.01, binding: fx((s) => s.sscs.intensity, (v) => ({ sscs: { intensity: v } })) },
      { id: "tolerance", kind: "slider", label: "Tolerance", min: 0.1, max: 5, step: 0.1, binding: fx((s) => s.sscs.tolerance, (v) => ({ sscs: { tolerance: v } })) },
      { id: "steps", kind: "slider", label: "Step Count", min: 1, max: 8, step: 1, binding: fx((s) => s.sscs.stepCount, (v) => ({ sscs: { stepCount: v } })) },
      { id: "onlyDebug", kind: "checkbox", label: "Debug only SSCS", binding: fx((s) => s.sscs.onlySSCSDebug, (v) => ({ sscs: { onlySSCSDebug: v } })) },
      { id: "halfRes", kind: "checkbox", label: "Half Resolution", binding: fx((s) => s.sscs.halfRes, (v) => ({ sscs: { halfRes: v } })) },
    ],
  },
  {
    id: "gemRefraction",
    title: "Gem Refraction",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.gemRefraction.enabled, (v) => ({ gemRefraction: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.gemRefraction.enabled, (v) => ({ gemRefraction: { enabled: v } })) },
    ],
  },
  // ─── Extras below: not in the iJewel Post Processing checklist. ───
  {
    id: "postMaster",
    title: "Post Effects",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.enabled, (v) => ({ enabled: v })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enable Post Effects", binding: fx((s) => s.enabled, (v) => ({ enabled: v })) },
    ],
  },
  {
    id: "reflectionProbe",
    title: "Reflection Probe",
    defaultCollapsed: true,
    enabledBadge: fx((s) => s.reflectionProbe.enabled, (v) => ({ reflectionProbe: { enabled: v } })),
    rows: [
      { id: "enabled", kind: "checkbox", label: "Enabled", binding: fx((s) => s.reflectionProbe.enabled, (v) => ({ reflectionProbe: { enabled: v } })) },
      { id: "asSsrFallback", kind: "checkbox", label: "Fill SSR misses", binding: fx((s) => s.reflectionProbe.asSsrFallback, (v) => ({ reflectionProbe: { asSsrFallback: v } })) },
      { id: "asMetalEnv", kind: "checkbox", label: "Reflect on metals", binding: fx((s) => s.reflectionProbe.asMetalEnv, (v) => ({ reflectionProbe: { asMetalEnv: v } })) },
      // Tier C-2: genuine diamond→diamond / diamond→metal reflection. Auto-enables
      // the probe so a stone has surroundings to reflect; recompiles gem materials.
      { id: "asGemEnv", kind: "checkbox", label: "Reflect surroundings in diamonds", binding: fx((s) => s.reflectionProbe.asGemEnv, (v) => ({ reflectionProbe: v ? { asGemEnv: true, enabled: true } : { asGemEnv: false } })) },
      { id: "gemProbeIntensity", kind: "slider", label: "Diamond reflection strength", min: 0, max: 1, step: 0.01, visible: (read) => (read("postFX") as { reflectionProbe: { asGemEnv: boolean } }).reflectionProbe.asGemEnv, binding: fx((s) => s.reflectionProbe.gemProbeIntensity, (v) => ({ reflectionProbe: { gemProbeIntensity: v } })) },
    ],
  },
  {
    id: "reflectionMatrix",
    title: "Reflection Matrix (per-object)",
    defaultCollapsed: true,
    rows: [{ id: "matrix", kind: "custom", label: "", component: ReflectionMatrixControl }],
  },
  {
    id: "debug",
    title: "Pipeline Debug",
    defaultCollapsed: true,
    rows: [
      { id: "view", kind: "dropdown", label: "Buffer", options: DEBUG_VIEWS, binding: fx((s) => s.debug.view, (v) => ({ debug: { view: v as PipelineDebugView } })) },
      { id: "split", kind: "slider", label: "Split", min: 0, max: 1, step: 0.01, binding: fx((s) => s.debug.split, (v) => ({ debug: { split: v } })) },
    ],
  },
];
